import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { CertificateHolder } from '../hooks/useCertificateHolders';
import { formatUsPhone } from '../../../utilities/formatPhone';

type ViewCertificateHolderProps = {
  holder: CertificateHolder;
  onBackToList: () => void;
};

function getInitials(name: string | null | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (parts[0]?.[0] || '?').toUpperCase();
}

function formatFullAddress(holder: CertificateHolder): string {
  const parts: string[] = [];
  if (holder.addressLine1) parts.push(holder.addressLine1);
  if (holder.addressLine2) parts.push(holder.addressLine2);
  const cityState: string[] = [];
  if (holder.city) cityState.push(holder.city);
  if (holder.stateName) cityState.push(holder.stateName);
  if (cityState.length > 0) {
    let cs = cityState.join(', ');
    if (holder.zipCode) cs += ' ' + holder.zipCode;
    parts.push(cs);
  }
  return parts.length > 0 ? parts.join(', ') : '-';
}

export default function ViewCertificateHolder({ holder, onBackToList }: ViewCertificateHolderProps) {
  const navigate = useNavigate();

  return (
    <div className="chView">
      <div className="chView__header">
        <button className="ch__backBtn" onClick={onBackToList}>
          <i className="pi pi-arrow-left" />
          <span>Back to List</span>
        </button>
        <button className="ch__backBtn" onClick={() => navigate('/agentdashboard')}>
          <i className="pi pi-home" />
          <span>Dashboard</span>
        </button>
      </div>

      <div className="chView__profile">
        <div className="chView__avatar">{getInitials(holder.name)}</div>
        <div className="chView__profileInfo">
          <h2 className="chView__name">{holder.name || '-'}</h2>
          <p className="chView__location">
            <i className="pi pi-map-marker" />
            <span>{[holder.city, holder.stateName].filter(Boolean).join(', ') || 'Location not specified'}</span>
          </p>
        </div>
      </div>

      <div className="chView__section">
        <div className="chView__sectionHeader">
          <div className="chView__sectionIcon">
            <i className="pi pi-id-card" />
          </div>
          <span className="chView__sectionTitle">Contact Information</span>
        </div>
        <div className="chView__grid">
          <div className="chView__field">
            <span className="chView__fieldLabel">EMAIL</span>
            <span className="chView__fieldValue">
              {holder.eMail ? (
                <a href={`mailto:${holder.eMail}`} className="chView__link">
                  <i className="pi pi-envelope" />
                  {holder.eMail}
                </a>
              ) : '-'}
            </span>
          </div>
          <div className="chView__field">
            <span className="chView__fieldLabel">PHONE</span>
            <span className="chView__fieldValue">
              <i className="pi pi-phone" />
              {formatUsPhone(holder.phone) || '-'}
            </span>
          </div>
          <div className="chView__field">
            <span className="chView__fieldLabel">CELL PHONE</span>
            <span className="chView__fieldValue">
              <i className="pi pi-mobile" />
              {formatUsPhone(holder.cellPhone) || '-'}
            </span>
          </div>
          <div className="chView__field">
            <span className="chView__fieldLabel">FAX</span>
            <span className="chView__fieldValue">
              <i className="pi pi-print" />
              {formatUsPhone(holder.fax) || '-'}
            </span>
          </div>
        </div>
      </div>

      <div className="chView__section">
        <div className="chView__sectionHeader">
          <div className="chView__sectionIcon">
            <i className="pi pi-map" />
          </div>
          <span className="chView__sectionTitle">Address Details</span>
        </div>
        <div className="chView__grid chView__grid--address">
          <div className="chView__field chView__field--full">
            <span className="chView__fieldLabel">ADDRESS LINE 1</span>
            <span className="chView__fieldValue">{holder.addressLine1 || '-'}</span>
          </div>
          <div className="chView__field chView__field--full">
            <span className="chView__fieldLabel">ADDRESS LINE 2</span>
            <span className="chView__fieldValue">{holder.addressLine2 || '-'}</span>
          </div>
          <div className="chView__field">
            <span className="chView__fieldLabel">CITY</span>
            <span className="chView__fieldValue">{holder.city || '-'}</span>
          </div>
          <div className="chView__field">
            <span className="chView__fieldLabel">STATE</span>
            <span className="chView__fieldValue">{holder.stateName || '-'}</span>
          </div>
          <div className="chView__field">
            <span className="chView__fieldLabel">ZIP CODE</span>
            <span className="chView__fieldValue">
              <span className="chView__zipBadge">{holder.zipCode || '-'}</span>
            </span>
          </div>
        </div>
      </div>

      <div className="chView__section">
        <div className="chView__sectionHeader">
          <div className="chView__sectionIcon">
            <i className="pi pi-directions" />
          </div>
          <span className="chView__sectionTitle">Full Address</span>
        </div>
        <div className="chView__fullAddress">
          {formatFullAddress(holder)}
        </div>
      </div>
    </div>
  );
}
